import type { BulkAclEntity } from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { referenceRepository } from "$root/functional-areas/portfolio/references-repository";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { universeWithGroupsRepository } from "$root/functional-areas/universe/universeWithGroupsRepository";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import { parallelize } from "$root/utils/promise";
import type { AclPreviewResponse, EntityMetadata } from "./types";

type PreviewFetcher = (acl: Array<BulkAclEntity>) => Promise<Record<string, unknown[]>>;

const previewByEntityType: Record<string, PreviewFetcher> = {
	INVESTMENT: (acl) => portfolioRepository.bulkAclPreview.fetchQuery(acl),
	REFERENCE: (acl) => referenceRepository.referenceBulkAclPreview.fetchQuery(acl),
	BENCHMARK: (acl) => referenceRepository.benchmarkBulkAclPreview.fetchQuery(acl),
	UNIVERSE: (acl) => universeRepository.universeBulkAclPreview.fetchQuery(acl),
	GROUP_UNIVERSE: (acl) => universeWithGroupsRepository.universeWithGroupBulkAclPreview.fetchQuery(acl),
	MARKET_VIEW: (acl) => marketViewRepository.bulkSharePreview.fetchQuery(acl),
};

export async function previewAclChanges(
	entityMetadataList: EntityMetadata[],
	proposedAcl: RichAccessControl[],
): Promise<AclPreviewResponse> {
	const entitiesByType = Map.groupBy(entityMetadataList, (m) => m.entityType);
	const entries = [...entitiesByType.entries()];

	const results = await parallelize(
		entries.map(([entityType, entities]) => {
			const fetcher = previewByEntityType[entityType];
			if (!fetcher) {
				return () => ({}) as Record<string, unknown[]>;
			}

			const bulkAclEntities: BulkAclEntity[] = entities.map((m) => ({
				entityUuid: m.uuid,
				accessControlList: proposedAcl,
			}));

			return () => fetcher(bulkAclEntities);
		}),
	);

	return Object.assign({}, ...results) as AclPreviewResponse;
}
